import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { services } from "@/data/services";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScript, defaultOgImageMeta } from "@/lib/seo";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Business Recycling & Secure Disposal | Preserve Earth" },
      {
        name: "description",
        content:
          "Recycling and disposal services for UK businesses: textile recycling, confidential waste, IT & media recycling, product destruction and one-off shredding.",
      },
      { property: "og:title", content: "Business Recycling & Secure Disposal | Preserve Earth" },
      {
        property: "og:description",
        content:
          "Recycling and disposal services for UK businesses: textile recycling, confidential waste, IT & media recycling, product destruction and one-off shredding.",
      },
      { name: "twitter:title", content: "Business Recycling & Secure Disposal | Preserve Earth" },
      { name: "twitter:description", content: "Recycling and disposal services for UK businesses: textile recycling, confidential waste, IT & media recycling, product destruction and one-off shredding." },
      { property: "og:url", content: "https://preserveearth.co.uk/services" },
      { property: "og:type", content: "website" },
      ...defaultOgImageMeta,
    ],
    links: [{ rel: "canonical", href: "https://preserveearth.co.uk/services" }],
    scripts: [breadcrumbScript([{ name: "Services", path: "/services" }])],
  }),
  component: ServicesIndex,
});

function ServicesIndex() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="container-page pb-24 pt-20">
          <Breadcrumbs crumbs={[{ name: "Services", path: "/services" }]} />
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">
            Services
          </p>
          <h1 className="max-w-3xl font-serif text-4xl text-charcoal md:text-5xl">
            Secure recycling & disposal services for UK businesses.
          </h1>
          <p className="mt-6 max-w-2xl text-pretty leading-relaxed text-charcoal/70">
            Explore our core services: textile recycling, confidential waste
            disposal, IT &amp; media recycling, secure product destruction and
            one-off shredding, across the UK. Every collection is logged,
            with an electronic note of secure destruction issued.
          </p>
          <ul className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-charcoal/70">
            {[
              "GDPR-compliant shredding",
              "Full audit trail",
              "Electronic note of secure destruction",
            ].map((item, i) => (
              <li key={item} className="flex items-center gap-3">
                {i > 0 && <span aria-hidden className="text-charcoal/25">·</span>}
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              hash="enquire"
              className="inline-flex items-center justify-center rounded-sm bg-moss px-6 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
            >
              Enquire about a collection
            </Link>
            <a
              href="mailto:info@preserveearth.co.uk"
              className="inline-flex items-center justify-center rounded-sm px-6 py-3 text-sm font-medium text-charcoal ring-1 ring-charcoal/15 transition-colors hover:bg-sage-light/40"
            >
              info@preserveearth.co.uk
            </a>
          </div>
        </section>


        <section className="container-page pb-32">
          <ol className="grid gap-px border border-charcoal/10 bg-charcoal/10">
            {services.map((s) => (
              <li
                key={s.slug}
                className="group flex flex-col items-start gap-6 bg-cream px-6 py-10 transition-colors hover:bg-sage-light/40 md:flex-row md:gap-10 md:px-10"
              >
                <span className="pt-2 font-mono text-xs font-medium tracking-[0.2em] text-moss">
                  {s.n} / 05
                </span>
                <div className="flex-1">
                  <h2 className="mb-3 font-serif text-2xl text-charcoal md:text-3xl">
                    {s.slug === "textile-recycling" ? <Link to="/services/$slug" params={{ slug: s.slug }}>{s.title}</Link> : s.title}
                  </h2>
                  <p className="max-w-[60ch] text-pretty leading-relaxed text-charcoal/70">
                    {s.short}
                  </p>
                </div>
                <Link
                  to="/services/$slug"
                  params={{ slug: s.slug }}
                  className="shrink-0 text-sm font-medium underline decoration-moss/40 underline-offset-4 transition-colors hover:decoration-moss md:w-64"
                >
                  Learn more →
                </Link>
              </li>
            ))}
          </ol>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
